// Page-lieu du tome 2 : image avec zones cliquables cachées (<area>), une bulle de texte par zone.
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import "../../css/EnigmeTome2.css";

// Marge minimale entre une bulle et le bord de la fenêtre (px)
const MARGE_BULLE = 8;

// Coordonnées <area> relevées sur l'image native → coordonnées de l'image affichée
const adjustCoords = (coords, scaleX, scaleY) => {
    if (!coords || typeof coords !== "string") {
        console.warn("Coordonnées invalides : ", coords);
        return "";
    }

    return coords
        .split(",")
        .map((value, index) =>
            Math.round(index % 2 === 0 ? value * scaleX : value * scaleY)
        )
        .join(",");
};

const Etome2 = ({ titre, enigmesData, imageSrc }) => {
    /////////////////////////////////////
    /////////      STATE      ///////////
    /////////////////////////////////////
    const location = useLocation();
    const adminMode = new URLSearchParams(location.search).has('fanelia');
    const imgRef = useRef(null);
    const bubbleRefs = useRef({});
    const [adminZoneClick, setAdminZoneClick] = useState("");
    // Taille affichée et native de l'image, mise à jour à chaque redimensionnement
    const [imgSize, setImgSize] = useState(null);
    // Bulles ouvertes : position du clic en fraction de l'image (suit l'image quand elle change de taille)
    const [visibleZones, setVisibleZones] = useState({});

    /////////////////////////////////
    /////      USEEFFECT      ///////
    /////////////////////////////////

    // Recalcul des zones au chargement de l'image et à chaque changement de taille
    // (fenêtre redimensionnée, téléphone tourné…)
    useEffect(() => {
        const img = imgRef.current;
        if (!img) return;

        const measure = () => {
            if (!img.naturalWidth || !img.offsetWidth) return;
            setImgSize({
                width: img.offsetWidth,
                height: img.offsetHeight,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
            });
        };

        img.addEventListener("load", measure);
        if (img.complete) measure();

        const observer = new ResizeObserver(measure);
        observer.observe(img);

        return () => {
            img.removeEventListener("load", measure);
            observer.disconnect();
        };
    }, [imageSrc]);

    // Échap ferme les bulles ouvertes
    useEffect(() => {
        const handleKey = (event) => {
            if (event.key === "Escape") setVisibleZones({});
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, []);

    // Garde les bulles dans la fenêtre : décalage appliqué après le rendu, avant l'affichage
    useLayoutEffect(() => {
        Object.values(bubbleRefs.current).forEach((bubble) => {
            if (!bubble) return;
            bubble.style.transform = "";
            const rect = bubble.getBoundingClientRect();
            const maxRight = document.documentElement.clientWidth - MARGE_BULLE;
            const maxBottom = window.innerHeight - MARGE_BULLE;

            let dx = 0;
            let dy = 0;
            if (rect.right > maxRight) dx = maxRight - rect.right;
            if (rect.left + dx < MARGE_BULLE) dx = MARGE_BULLE - rect.left;
            if (rect.bottom > maxBottom) dy = maxBottom - rect.bottom;
            if (rect.top + dy < MARGE_BULLE) dy = MARGE_BULLE - rect.top;

            if (dx || dy) bubble.style.transform = `translate(${dx}px, ${dy}px)`;
        });
    }, [visibleZones, imgSize]);

    const handleImageClick = (event) => {
        if (adminMode && imgSize) {
            const rect = event.currentTarget.getBoundingClientRect();
            const x = Math.round((event.clientX - rect.left) * imgSize.naturalWidth / imgSize.width);
            const y = Math.round((event.clientY - rect.top) * imgSize.naturalHeight / imgSize.height);

            setAdminZoneClick(`X=${x}, Y=${y}`);
            console.log(`Coordonnées ajustées de clic : X=${x}, Y=${y}`);
        }
    };

    const handleAreaClick = (zoneKey, event) => {
        event.preventDefault();
        const rect = imgRef.current.getBoundingClientRect();
        const position = {
            fx: (event.clientX - rect.left) / rect.width,
            fy: (event.clientY - rect.top) / rect.height,
        };

        setVisibleZones((prevZones) => ({
            ...prevZones,
            [zoneKey]: prevZones[zoneKey] ? null : position,
        }));
    };

    const handleBubbleClick = (zoneKey) => {
        setVisibleZones((prevZones) => ({
            ...prevZones,
            [zoneKey]: null,
        }));
    };

    const scaleX = imgSize ? imgSize.width / imgSize.naturalWidth : 0;
    const scaleY = imgSize ? imgSize.height / imgSize.naturalHeight : 0;

    return (
        <div className='text-center'>
            <h1>{titre}</h1>

            {adminMode && <p>Coordonnées du dernier clic : {adminZoneClick}</p>}

            <div className="secret_wrapper">
                <img
                    ref={imgRef}
                    className="secret_img"
                    src={imageSrc}
                    useMap="#image-map"
                    alt="secret"
                    onClick={handleImageClick}
                />

                <map name="image-map">
                    {imgSize && Object.entries(enigmesData).map(([key, { title, coords, shape }]) => (
                        <area
                            key={key}
                            alt={title}
                            title={title}
                            coords={adjustCoords(coords, scaleX, scaleY)}
                            shape={shape}
                            onClick={(e) => handleAreaClick(key, e)}
                        />
                    ))}
                </map>

                {imgSize && Object.entries(visibleZones).map(([key, position]) =>
                    position ? (
                        <div
                            key={key}
                            ref={(el) => {
                                if (el) bubbleRefs.current[key] = el;
                                else delete bubbleRefs.current[key];
                            }}
                            className="speech-bubble"
                            style={{
                                top: `${position.fy * imgSize.height}px`,
                                left: `${position.fx * imgSize.width}px`,
                            }}
                            onClick={() => handleBubbleClick(key)}
                        >
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: enigmesData[key].text,
                                }}
                            />
                        </div>
                    ) : null
                )}
            </div>
        </div>
    );
};

export default Etome2;
